import React, {useCallback, useEffect, useReducer, useRef} from 'react';

import {useI18n} from '@udemy/i18n';
import {Modal} from '@udemy/react-dialog-components';

import styles from './at-risk-feedback-modal.module.less';
import {AT_RISK_STEP_ID} from './constants';
import {
    trackAtRiskHelpCtaSelected,
    trackAtRiskHelpModalPresented,
    type AtRiskHelpCtaType,
} from './events';
import {useAtRiskSurvey} from './hooks/use-at-risk-survey';
import {renderAtRiskStep} from './step-renderer';

import type {AtRiskWizardAction, AtRiskWizardState} from './types';

export interface AtRiskFeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    surveyCode: string;
    lastCourseUrl?: string;
    lastCourseId?: string;
    source?: string;
    onSurveyLoaded?: (result: {hasError: boolean}) => void;
}

const INITIAL_STATE: AtRiskWizardState = {
    currentStepId: AT_RISK_STEP_ID.INITIAL,
    selectedOption: null,
    freeformText: '',
    isSubmitting: false,
    submitError: null,
};

function reducer(state: AtRiskWizardState, action: AtRiskWizardAction): AtRiskWizardState {
    switch (action.type) {
        case 'OPTION_SELECTED':
            return {...state, selectedOption: action.value, submitError: null};
        case 'STEP_ADVANCED':
            return {
                ...state,
                currentStepId: action.nextStepId,
                isSubmitting: false,
                submitError: null,
            };
        case 'FREEFORM_UPDATED':
            return {...state, freeformText: action.text};
        case 'SUBMITTING':
            return {...state, isSubmitting: true, submitError: null};
        case 'SUBMIT_ERROR':
            return {...state, isSubmitting: false, submitError: action.error};
        case 'RESET':
            return INITIAL_STATE;
        default:
            return state;
    }
}

export function AtRiskFeedbackModal({
    isOpen,
    onClose,
    surveyCode,
    lastCourseUrl,
    lastCourseId,
    source,
    onSurveyLoaded,
}: AtRiskFeedbackModalProps) {
    const {gettext} = useI18n();
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const {submitAnswer, answerOptions, isLoading, error} = useAtRiskSurvey(surveyCode);
    const hasFiredSurveyLoadedRef = useRef(false);
    const hasTrackedPresentedRef = useRef(false);

    useEffect(() => {
        if (isLoading || hasFiredSurveyLoadedRef.current) return;
        hasFiredSurveyLoadedRef.current = true;
        onSurveyLoaded?.({hasError: !!error});
    }, [isLoading, error, onSurveyLoaded]);

    useEffect(() => {
        if (!isOpen) {
            hasTrackedPresentedRef.current = false;
            return;
        }
        if (hasTrackedPresentedRef.current) return;
        hasTrackedPresentedRef.current = true;
        trackAtRiskHelpModalPresented(source);
    }, [isOpen, source]);

    const handleClose = useCallback(() => {
        dispatch({type: 'RESET'});
        onClose();
    }, [onClose]);

    const onTrackEventClick = useCallback(
        (ctaType: AtRiskHelpCtaType) => {
            trackAtRiskHelpCtaSelected(ctaType, state.currentStepId, source);
        },
        [state.currentStepId, source],
    );

    const stepProps = {
        state,
        dispatch,
        onClose: handleClose,
        lastCourseUrl,
        lastCourseId,
        source,
        onTrackEventClick,
        submitAnswer,
        answerOptions,
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title=""
            renderTitle={() => [
                'at-risk-modal-label',
                <span key="t" id="at-risk-modal-label" className="ud-sr-only">
                    {gettext("How's your learning going?")}
                </span>,
            ]}
            className={styles['modal']}
            requireExplicitAction={true}
        >
            {renderAtRiskStep(state.currentStepId, stepProps)}
        </Modal>
    );
}
